/**
 * Akash OpenAI-compatible text generation provider
 * https://chatapi.akash.network/api/v1/chat/completions
 */

import OpenAI from 'openai';
import { BaseProvider } from '../../core/base-provider';
import {
  TextGenerationOptions,
  TextResponse,
  UsageStats,
  ProviderError,
  Message,
} from '../../core/types';

export interface AkashTextProvider extends BaseProvider {
  generate(prompt: string, options?: TextGenerationOptions): Promise<TextResponse>;
  generateWithMessages(messages: Message[], options?: TextGenerationOptions): Promise<TextResponse>;
  stream(prompt: string, options?: TextGenerationOptions): AsyncGenerator<string>;
}

export class AkashText extends BaseProvider implements AkashTextProvider {
  private client?: OpenAI;

  constructor() {
    super('akash-text', 'Akash Text', 'text');
  }

  protected async onInitialize(): Promise<void> {
    if (!this.config.apiKey) {
      throw new ProviderError('Akash API key is required', this.id);
    }

    this.client = new OpenAI({
      apiKey: this.config.apiKey,
      baseURL: this.config.baseUrl || 'https://chatapi.akash.network/api/v1',
      timeout: this.config.timeout || 30000,
      maxRetries: this.config.maxRetries || 3,
    });
  }

  protected async checkAvailability(): Promise<boolean> {
    try {
      const models = await this.client!.models.list();
      return models.data.length > 0;
    } catch {
      return false;
    }
  }

  protected async fetchUsage(): Promise<UsageStats> {
    return {
      tokensUsed: 0,
      requestsCount: 0,
      cost: 0,
    };
  }

  async generate(prompt: string, options?: TextGenerationOptions): Promise<TextResponse> {
    this.ensureInitialized();

    const messages: Message[] = [];
    if (options?.systemPrompt) {
      messages.push({ role: 'system', content: options.systemPrompt });
    }
    messages.push({ role: 'user', content: prompt });

    return this.generateWithMessages(messages, options);
  }

  async generateWithMessages(
    messages: Message[],
    options?: TextGenerationOptions
  ): Promise<TextResponse> {
    this.ensureInitialized();

    try {
      const completion = await this.client!.chat.completions.create({
        model: options?.model || 'llama-3.1-8b-instruct',
        messages: messages.map((m) => ({
          role: m.role,
          content: m.content,
        })),
        temperature: options?.temperature || 0.7,
        max_tokens: options?.maxTokens || 2048,
        top_p: options?.topP,
        frequency_penalty: options?.frequencyPenalty,
        presence_penalty: options?.presencePenalty,
        stop: options?.stop,
        stream: false,
      });

      const choice = completion.choices[0];
      return {
        text: choice.message.content || '',
        model: completion.model,
        usage: completion.usage
          ? {
              promptTokens: completion.usage.prompt_tokens,
              completionTokens: completion.usage.completion_tokens,
              totalTokens: completion.usage.total_tokens,
            }
          : undefined,
        finishReason: choice.finish_reason || undefined,
      };
    } catch (error: any) {
      if (error.status === 429) {
        throw new ProviderError('Rate limit exceeded', this.id, {
          retryAfter: error.headers?.['retry-after'],
        });
      }
      throw new ProviderError(error.message || 'Failed to generate text', this.id, error);
    }
  }

  async *stream(prompt: string, options?: TextGenerationOptions): AsyncGenerator<string> {
    this.ensureInitialized();

    const messages: Message[] = [];
    if (options?.systemPrompt) {
      messages.push({ role: 'system', content: options.systemPrompt });
    }
    messages.push({ role: 'user', content: prompt });

    try {
      const stream = await this.client!.chat.completions.create({
        model: options?.model || 'llama-3.1-8b-instruct',
        messages: messages.map((m) => ({
          role: m.role,
          content: m.content,
        })),
        temperature: options?.temperature || 0.7,
        max_tokens: options?.maxTokens || 2048,
        top_p: options?.topP,
        frequency_penalty: options?.frequencyPenalty,
        presence_penalty: options?.presencePenalty,
        stop: options?.stop,
        stream: true,
      });

      for await (const chunk of stream) {
        const content = chunk.choices[0]?.delta?.content;
        if (content) {
          yield content;
        }
      }
    } catch (error: any) {
      throw new ProviderError(error.message || 'Failed to stream text', this.id, error);
    }
  }
}